import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { SUPPORTED_LANGS, DEFAULT_LANG, stripLangPrefix, localizedPath } from "../../i18n";

export const SITE_ORIGIN = "https://apuseniactive.ro";
const DEFAULT_OG_IMAGE = SITE_ORIGIN + "/images/holidays/hero-ridge.webp";
const OG_LOCALE = { ro: "ro_RO", en: "en_GB", de: "de_DE", hu: "hu_HU" };

/**
 * Per-page SEO block: title, description, hreflang, canonical, og/twitter tags.
 * `titleKey`/`descKey` are i18n keys under `meta.*`; `title`/`description` override them.
 * `path` should be a clean path without language prefix (e.g. "/experiente").
 */
export default function SEO({ titleKey, descKey, title: titleProp, description: descProp, path = null, image = null, extraJsonLd = null }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const cleanPath = path || stripLangPrefix(location.pathname);
  const lang = i18n.language || DEFAULT_LANG;

  const canonical = SITE_ORIGIN + localizedPath(cleanPath, lang);
  const title = titleProp || t(`meta.${titleKey}`, { defaultValue: t("meta.home.title") });
  const description = descProp || t(`meta.${descKey}`, { defaultValue: t("meta.home.description") });
  const ogImage = image ? (image.startsWith("http") ? image : SITE_ORIGIN + image) : DEFAULT_OG_IMAGE;
  const isDefaultImage = ogImage === DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={canonical} />

      {SUPPORTED_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={SITE_ORIGIN + localizedPath(cleanPath, l)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={SITE_ORIGIN + localizedPath(cleanPath, DEFAULT_LANG)} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Apuseni Active" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      {isDefaultImage && <meta property="og:image:width" content="1600" />}
      {isDefaultImage && <meta property="og:image:height" content="739" />}
      <meta property="og:locale" content={OG_LOCALE[lang] || "en_GB"} />
      {SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {extraJsonLd && <script type="application/ld+json">{JSON.stringify(extraJsonLd)}</script>}
    </Helmet>
  );
}
// Organization / WebSite JSON-LD is static in public/index.html so it is visible without JavaScript.
